
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { API_ROUTES } from "@/utils/API";
import {
  API_TRoleTemplate,
  TRoleTemplate_Table,
  convertRoleTemplatesForTable,
} from "@/models/role_templates";
import { TCase_Create } from "@/models/cases";

import { SMRoles, SMTasks } from "@/store";
import { SPONSOR_LICENCE_SERVICE } from "@/constants/services";
import { TTab } from "@/models/tabs";
import { JOB_TEMPLATES_TAB, ARCHIVED_JOB_TEMPLATES_TAB } from "@/constants/compliance";

const EMPLOYER_ROLE_ROUTE = "EmployerRole";

const SUCCESS_NOTIFICATION_TYPE = "success";

const SERVICE_ROUTE = "CandidateService";

const BAD_REQUEST_HTTP_STATUS_CODE = 400;

const TABLE_INITIAL_LIMIT = 20;
const TABLE_INITIAL_OFFSET = 0;
const TABLE_INITIAL_ORDER = "desc";
const TABLE_INITIAL_ORDER_BY = "id";

const TABLE_ROW_CLASS = "role-templates-table-row";

type ParamsKeyObject = {
  [key: string]: any;
};

@Component({
  components: {
    Tabs: () => import("@/components/Tabs/Tabs.vue"),
    NotificationJobTemplatesArchived: () =>
      import("@/components/Notifications/NotificationJobTemplatesArchived.vue"),
    NotificationJobTemplates: () =>
      import("@/components/Notifications/NotificationJobTemplates.vue"),
  },
})
export default class JobTemplatesTableTabs extends Vue {
  @Prop({ type: Boolean, default: false }) readonly selecting?: boolean;
  @Prop({ type: String }) readonly service?: string;
  @Prop({ type: Number }) readonly serviceFromId?: number;
  @Prop({ type: Array, default: () => [] }) readonly tabValues!: string[];
  @Prop({ type: Object, default: null }) readonly tabNames!: ParamsKeyObject;
  @Prop({ type: Array, default: () => [] }) mainItems!: TRoleTemplate_Table[];
  @Prop({ type: Array, default: () => [] }) sideItems!: TRoleTemplate_Table[];
  @Prop({ type: Array, default: () => [] }) mainItemsInactive!: TRoleTemplate_Table[];
  @Prop({ type: Array, default: () => [] }) sideItemsInactive!: TRoleTemplate_Table[];
  @Prop({ type: Boolean, default: true }) readonly countSideItemsInactive?: boolean;
  @Prop({ type: Boolean, default: true }) readonly showDigit!: boolean;
  @Prop({ type: Object, default: null }) readonly actions?: any;
  @Prop({ type: Boolean, default: false }) readonly showActions!: boolean;

  get tabs(): TTab[] {
    return this.tabValues.map((name) => ({
      label: (this.taskTabNames as any)[name]?.toUpperCase(),
      isActive: this.currentTabName === name,
      digit: this.showDigit ? this.getTasksCount(name) : 0,
      action: () => {
        this.currentTabName = name;
        this.componentKey += 1;
      },
    }));
  }

  get attributes() {
    const {
      headers,
      labels,
      mainItems,
      sideItems,
      mainItemsInactive,
      sideItemsInactive,
      currentTabName,
      mainTabName,
    } = this;

    return {
      headers,
      items: currentTabName === mainTabName ? mainItems : sideItems,
      inactiveItems: currentTabName === mainTabName ? mainItemsInactive : sideItemsInactive,
      labels,
    };
  }

  get taskTabNames() {
    if (this.tabNames) return this.tabNames;

    const tabNames: ParamsKeyObject = {};

    this.tabValues.forEach((value) => {
      tabNames[value] = value.replace("_", " ").toUpperCase();
    });

    return tabNames;
  }

  get currentTaskTabName() {
    if (!this.tabValues.includes(SMTasks.tabName)) return this.tabValues[0];

    return SMTasks.tabName || this.tabValues[0];
  }

  @Watch("currentTabName", { immediate: true })
  currentTabNameChanged(value: string) {
    this.$emit("set-tab-name", value);
  }

  mainTabName = this.tabValues[0];

  currentTabName = this.currentTaskTabName;

  componentKey = 0;

  get isMainTab() {
    return this.mainTabName === this.currentTabName;
  }

  get hasActions() {
    return this.actions && Object.keys(this.actions).length;
  }

  get isActionsShown() {
    if (!this.hasActions) return false;

    if (this.showActions) return !!this.actions;

    return this.actions;
  }

  getTasksCount(name: string): number {
    return name == this.mainTabName ? this.mainItems.length : this.countSideItems();
  }

  countSideItems() {
    return this.sideItems.length + this.sideItemsInactive.length;
  }

  warningPopup = false;

  created() {
    this.items = this.attributes.items;
    this.$emit("loaded", this.items);
    this.loading = false;
    this.init = true;
  }

  options = {
    infiniteScroll: true,
    filtersApi: true,
    sortApi: true,
  };

  labels = {
    allLabel: this.$t("table.allLabel"),
    placeholderLabel: this.$t("table.placeholderLabel"),
  };

  params: ParamsKeyObject = {
    limit: TABLE_INITIAL_LIMIT,
    offset: TABLE_INITIAL_OFFSET,
    order: TABLE_INITIAL_ORDER,
    orderBy: TABLE_INITIAL_ORDER_BY,
  };

  headers = [
    {
      text: this.$t("table.headers.templateName"),
      value: "job_template_title",
      filterable: true,
      slot: true,
    },
    {
      text: this.$t("table.headers.socGroup"),
      value: "soc_group_name",
      filterable: true,
      slot: true,
    },
    {
      text: this.$t("table.headers.jobTitle"),
      value: "job_title",
      filterable: true,
      slot: true,
    },
  ];

  getRowClasses(item) {
    return {
      [`${TABLE_ROW_CLASS}_draft`]: !item.completed,
    };
  }

  items: TRoleTemplate_Table[] = [];

  loading = false;

  init = false;

  async getTableItems(config = null) {
    try {
      this.loading = true;

      const params = {
        archive: false,
      };

      const { data } = await this.axios.get(API_ROUTES.ROLE_TEMPLATES(), config ?? { params });

      if (!data) return;

      this.items = data.map((item: API_TRoleTemplate) => convertRoleTemplatesForTable(item));

      this.$emit("loaded", this.items);
    } finally {
      this.loading = false;

      this.init = true;
    }
  }

  get isHideEmptyTabs(): boolean {
    const { sideItemsInactive } = this;

    if (sideItemsInactive?.length) return false;

    return true;
  }

  get showNotificationJobTemplatesArchived(): boolean {
    return this.currentTabName === ARCHIVED_JOB_TEMPLATES_TAB;
  }

  get showNotificationJobTemplates(): boolean {
    const { currentTabName, mainItems, sideItemsInactive } = this;

    return (
      currentTabName === JOB_TEMPLATES_TAB && !mainItems?.length && !!sideItemsInactive?.length
    );
  }

  get showTabs(): boolean {
    const { mainItems, sideItemsInactive } = this;

    return !!mainItems?.length || !!sideItemsInactive?.length;
  }

  get showTable(): boolean {
    const { mainItems, sideItemsInactive, currentTabName } = this;

    if (currentTabName === JOB_TEMPLATES_TAB && !mainItems?.length) return false;

    return !!mainItems?.length || !!sideItemsInactive?.length;
  }

  get showPlaceholder() {
    const { mainItems, sideItemsInactive, loading } = this;

    return !mainItems?.length && !sideItemsInactive?.length && !loading;
  }

  get isArchive(): boolean {
    return this.currentTabName === ARCHIVED_JOB_TEMPLATES_TAB;
  }

  atRowClick(item: TRoleTemplate_Table) {
    SMTasks.setTabName(this.currentTabName);

    this.$emit("row-click", { item, disabled: this.isArchive });
  }

  goToRole(id: number) {
    this.$router.push({
      name: EMPLOYER_ROLE_ROUTE,
      params: { id: String(id) },
    });
  }

  openWarningPopup() {
    this.warningPopup = true;
  }

  closeWarningPopup() {
    this.warningPopup = false;
  }

  setLoading(id: number, isLoading: boolean) {
    const item = this.items.find((item) => item.id === id);

    if (!item) return;

    item.loading = isLoading;
  }

  async startService(id: number) {
    try {
      if (!this.service) return console.log(this.$tc("serviceNotSelectedWaring"));

      this.setLoading(id, true);

      const params = {
        target_id: id,
      };

      if (this.service === SPONSOR_LICENCE_SERVICE) {
        Object.assign(params, {
          case_from_id: this.serviceFromId,
        });
      }

      const { data } = await this.axios.get<number | TCase_Create>(
        API_ROUTES.CASE_CREATE(this.service, params)
      );

      if (!data) return;

      let caseId = 0;

      if (typeof data === "number") caseId = data;

      if (typeof data === "object") caseId = data?.case_id ?? 0;

      this.setLoading(id, false);

      if (!caseId) return;

      await this.$router.push({
        name: SERVICE_ROUTE,
        params: {
          id: String(caseId),
        },
      });

      const title = this.$tc(`notification.created.${this.service}`);

      this.$root.$emit("show-notification", {
        type: SUCCESS_NOTIFICATION_TYPE,
        title,
      });
    } catch ({ response }) {
      if ((response as Response).status === BAD_REQUEST_HTTP_STATUS_CODE) {
        this.openWarningPopup();
      }
    } finally {
      this.setLoading(id, false);
    }
  }

  async startServiceWithLastCreated() {
    const { items } = this;

    const lastCreatedItem = items[items.length - 1];

    await this.startService(Number(lastCreatedItem.id));
  }

  select(id: number) {
    if (!id) return;

    return this.selecting ? this.startService(id) : this.goToRole(id);
  }

  choose(item: TRoleTemplate_Table) {
    const { id } = item;

    if (this.selecting || !id) return;

    this.goToRole(id);
  }

  async complete(event: any, target_id: number, user_flow_id: number) {
    if (event.stopPropagation) event.stopPropagation();

    await SMRoles.editRoleTemplate({ target_id, user_flow_id });
  }

  firstLetterToUpperCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
}
